import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Check, Copy, Link2, Loader2, Network, QrCode, RefreshCw, Store, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { QrCode as QrCanvas } from '@/components/admin/QrCode';
import {
  type OnboardingLinkGroup,
  useCreateLinkFor,
  useDeactivateLinkAny,
  useMarkGroup,
  useOnboardingLinkGroups,
  useRotateLinkAny,
} from '@/hooks/admin/useOnboardingLinks';

/**
 * /admin/links — assign a public new-store link to a chain or a franchise
 * company, in one place.
 *
 * Both kinds of group can hold a link, and which one it is changes what the
 * link means to whoever opens it:
 *
 *   Chain HQ        one link for the whole brand. Whoever opens it could be
 *                   corporate or any franchisee, so the form has to ask.
 *   Franchise owner one link for ONE company that already buys from us. The
 *                   link IS the identity — no lookup, no email matching, no
 *                   chance of filing a store against the wrong company.
 *
 * The second is why this page exists. Identifying a returning franchisee from
 * their email address fails in both directions — an operator on a gmail
 * address can't be matched at all, and a shared brand domain matches the wrong
 * company — so the reliable answer is to hand each franchisee their own URL.
 *
 * Marking an account as a group lives here too. "Assign a link to a franchise"
 * is really two steps, and making someone go find the customer in another tab
 * to do the first one is how the second never happens.
 */

type Filter = 'all' | 'chain' | 'franchise' | 'unmarked';

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'all', label: 'Everything' },
  { key: 'chain', label: 'Chains' },
  { key: 'franchise', label: 'Franchise companies' },
  { key: 'unmarked', label: 'Not marked yet' },
];

function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return 'never';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function AdminLinks() {
  const { data, isLoading, error } = useOnboardingLinkGroups();
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const groups = data?.groups ?? [];

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return groups.filter((g) => {
      if (filter === 'unmarked' && g.group_kind !== null) return false;
      if ((filter === 'chain' || filter === 'franchise') && g.group_kind !== filter) return false;
      if (term && !`${g.name} ${g.group_brand ?? ''}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [groups, filter, q]);

  const counts = useMemo(
    () => ({
      chain: groups.filter((g) => g.group_kind === 'chain').length,
      franchise: groups.filter((g) => g.group_kind === 'franchise').length,
      live: groups.filter((g) => g.link).length,
    }),
    [groups],
  );

  return (
    <div className="space-y-5">
      {/* What a link is, before the table of them */}
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4">
        <h2 className="font-display text-sm font-semibold text-brix-ink inline-flex items-center gap-2">
          <Link2 size={15} className="text-brix-primary" />
          New-store links
        </h2>
        <p className="text-[13px] text-brix-muted mt-1.5 max-w-3xl">
          A public URL that opens a new-store form already attached to one account. Give a{' '}
          <b className="text-brix-ink">chain</b> its link and corporate can put it in their
          onboarding packet for any store. Give a <b className="text-brix-ink">franchise company</b>{' '}
          its own link and the store that comes back is already theirs — no name lookup, no
          matching them by email, no chance of billing a store to the wrong owner.
        </p>
        <div className="flex items-center gap-4 mt-3 text-[12px] text-brix-muted flex-wrap">
          <span>{counts.chain} chain{counts.chain === 1 ? '' : 's'}</span>
          <span>·</span>
          <span>{counts.franchise} franchise compan{counts.franchise === 1 ? 'y' : 'ies'}</span>
          <span>·</span>
          <span>{counts.live} live link{counts.live === 1 ? '' : 's'}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-[13px] font-semibold border cursor-pointer transition-colors',
              filter === f.key
                ? 'bg-brix-primary-tint text-brix-primary border-brix-primary/40'
                : 'text-brix-muted border-brix-border hover:text-brix-ink hover:bg-brix-primary-tint/40',
            )}
          >
            {f.label}
          </button>
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search accounts…"
          className="ml-auto h-9 w-56 rounded-lg border border-brix-border bg-brix-surface px-3 text-[13px] text-brix-ink placeholder:text-brix-muted/60"
        />
      </div>

      {notice && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-3.5 py-2.5 text-[13px] text-sky-900">
          {notice}
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-brix-warning/40 bg-brix-warning/5 px-3.5 py-2.5 text-[13px] text-brix-warning">
          Couldn't load links: {error instanceof Error ? error.message : 'unknown error'}
        </div>
      )}

      {isLoading ? (
        <div className="text-[13px] text-brix-muted inline-flex items-center gap-2 px-1">
          <Loader2 size={14} className="animate-spin" /> Loading accounts…
        </div>
      ) : shown.length === 0 ? (
        <div className="rounded-xl border border-dashed border-brix-border px-5 py-10 text-center">
          <p className="text-[13px] text-brix-muted">
            {q || filter !== 'all'
              ? 'Nothing matches that filter.'
              : 'No chains or franchise companies yet — a QBO customer with sub-customers shows up here.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((g) => (
            <GroupRow key={g.customer_id} group={g} onNotice={setNotice} />
          ))}
        </div>
      )}
    </div>
  );
}

function GroupRow({
  group,
  onNotice,
}: {
  group: OnboardingLinkGroup;
  onNotice: (m: string | null) => void;
}) {
  const mark = useMarkGroup();
  const create = useCreateLinkFor();
  const rotate = useRotateLinkAny();
  const deactivate = useDeactivateLinkAny();

  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const link = group.link;
  const label = group.group_brand ?? group.name;
  const busy = mark.isPending || create.isPending || rotate.isPending || deactivate.isPending;

  const run = (p: Promise<unknown>, ok: string) => {
    onNotice(null);
    p.then(() => onNotice(ok)).catch((e: unknown) =>
      onNotice(e instanceof Error ? e.message : 'Something went wrong.'),
    );
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      onNotice('Copy failed — select the link and copy it by hand.');
    }
  };

  /**
   * "Delete" this row — there's no account to delete (it's an ordinary QBO
   * customer), so this un-marks it as a chain/franchise group instead: the
   * live link (if any) is turned off first, then group_kind clears and the
   * row drops out of the Chains/Franchise-companies filters. Every enabled
   * sub-customer under it derives its locked settings from THIS master's
   * group_kind, so un-marking a group with real members un-locks them
   * immediately — worth a sharper confirm than the other actions here.
   */
  const unmark = async () => {
    const memberWarning =
      group.member_count > 0
        ? `\n\n${group.member_count} sub-customer${group.member_count === 1 ? '' : 's'} in QuickBooks ` +
          `currently derive locked settings from this account — removing it un-locks those immediately.`
        : '';
    const linkWarning = link ? ' The live link will be turned off first.' : '';
    if (!window.confirm(`Remove ${label} from the chain/franchise list?${linkWarning}${memberWarning}`)) return;
    onNotice(null);
    try {
      if (link) await deactivate.mutateAsync({ link_id: link.id });
      await mark.mutateAsync({ customer_id: group.customer_id, group_kind: null });
      onNotice(`${label} removed from the list.`);
    } catch (e) {
      onNotice(e instanceof Error ? e.message : 'Something went wrong.');
    }
  };

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4 space-y-3">
      {/* Identity */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/admin/customers/${group.customer_id}/overview`}
              className="font-display text-[15px] font-semibold text-brix-ink hover:text-brix-primary inline-flex items-center gap-1.5"
            >
              {group.group_kind === 'franchise' ? <Store size={14} /> : <Building2 size={14} />}
              {group.name}
            </Link>
            {group.group_kind === 'chain' && <StatusBadge tone="info" size="sm">Chain HQ</StatusBadge>}
            {group.group_kind === 'franchise' && <StatusBadge tone="info" size="sm">Franchise owner</StatusBadge>}
            {group.group_kind === null && (
              <span className="text-[11px] font-semibold text-brix-muted border border-brix-border rounded-md px-2 py-0.5">
                Not marked
              </span>
            )}
            {link ? (
              <StatusBadge tone="success" size="sm">Link live · used {link.uses_count}×</StatusBadge>
            ) : (
              <span className="text-[11px] text-brix-muted">No link</span>
            )}
          </div>
          <p className="text-[12px] text-brix-muted mt-1">
            {group.group_brand ? `${group.group_brand} · ` : ''}
            QBO #{group.qbo_customer_id ?? '—'} · {group.member_count} sub-customer
            {group.member_count === 1 ? '' : 's'} in QuickBooks
            {group.retired_links > 0 && ` · ${group.retired_links} retired link${group.retired_links === 1 ? '' : 's'}`}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {group.group_kind === null ? (
            <>
              <span className="text-[12px] text-brix-muted">Mark as</span>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() =>
                  run(
                    mark.mutateAsync({ customer_id: group.customer_id, group_kind: 'chain' }),
                    `${group.name} is now a Chain HQ — create its link below.`,
                  )
                }
              >
                <Building2 size={13} /> Chain HQ
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() =>
                  run(
                    mark.mutateAsync({ customer_id: group.customer_id, group_kind: 'franchise' }),
                    `${group.name} is now a Franchise owner — create its link below.`,
                  )
                }
              >
                <Store size={13} /> Franchise owner
              </Button>
            </>
          ) : !link ? (
            <Button
              variant="primary"
              size="sm"
              disabled={busy}
              onClick={() =>
                run(
                  create.mutateAsync({ master_customer_id: group.customer_id }),
                  `Link created for ${label}. Copy it and hand it over.`,
                )
              }
            >
              {create.isPending ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}
              Create link
            </Button>
          ) : (
            <>
              <Button variant="secondary" size="sm" onClick={() => void copy()}>
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setShowQr((v) => !v)}>
                <QrCode size={13} /> {showQr ? 'Hide QR' : 'QR'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => {
                  if (
                    window.confirm(
                      `Replace ${label}'s link? The current URL stops working immediately — ` +
                        'anyone holding it will need the new one.',
                    )
                  ) {
                    run(
                      rotate.mutateAsync({ link_id: link.id }),
                      `New link minted for ${label}. The old URL is dead.`,
                    );
                  }
                }}
              >
                {rotate.isPending ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                Rotate
              </Button>
              <button
                type="button"
                disabled={busy}
                className="text-[12px] text-brix-muted underline cursor-pointer hover:text-brix-ink"
                onClick={() => {
                  if (window.confirm(`Turn off ${label}'s link? Nobody will be able to file a store through it.`)) {
                    run(deactivate.mutateAsync({ link_id: link.id }), `${label}'s link is off.`);
                  }
                }}
              >
                Turn off
              </button>
            </>
          )}
          {group.group_kind !== null && (
            <button
              type="button"
              disabled={busy}
              className="text-[12px] text-brix-muted underline cursor-pointer hover:text-red-600 inline-flex items-center gap-1"
              onClick={() => void unmark()}
            >
              <X size={12} /> Remove from list
            </button>
          )}
        </div>
      </div>

      {/* The URL itself */}
      {link && (
        <div className="flex items-start gap-4 flex-wrap">
          <div className="min-w-[18rem] flex-1 space-y-1.5">
            <code className="block rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3 py-2 text-[12px] font-mono text-brix-ink break-all">
              {link.url}
            </code>
            <p className="text-[11.5px] text-brix-muted">
              Created {fmtWhen(link.created_at)}
              {link.created_by ? ` by ${link.created_by}` : ''} · last used {fmtWhen(link.last_used_at)}
              {link.expires_at ? ` · expires ${fmtWhen(link.expires_at)}` : ''} ·{' '}
              <Link
                to={`/admin/customers/${group.customer_id}/overview`}
                className="text-brix-primary font-semibold"
              >
                settings
              </Link>
            </p>
          </div>
          {showQr && (
            <div className="shrink-0 rounded-lg border border-brix-border bg-white p-2">
              <QrCanvas value={link.url} size={112} />
            </div>
          )}
        </div>
      )}

      {/* What this link will mean to whoever opens it */}
      {group.group_kind && (
        <p className="text-[12px] text-brix-muted border-l-2 border-brix-border pl-3">
          {group.group_kind === 'franchise' ? (
            <>
              <Store size={11} className="inline mb-0.5 mr-1" />
              Opens as <b className="text-brix-ink">a new location for {label}</b> — already
              identified, already approved. Nothing is asked twice.
            </>
          ) : (
            <>
              <Network size={11} className="inline mb-0.5 mr-1" />
              Opens as <b className="text-brix-ink">a new {label} store</b>, billing on the {label}{' '}
              account. Anyone in the brand may be holding it, so it stays generic — a franchisee
              who bills separately needs their own link instead.
            </>
          )}
        </p>
      )}
    </div>
  );
}
